import React, { createContext, useState, useEffect, ReactNode, FC, useCallback } from 'react'
import { Spin } from 'antd'
import * as Sentry from '@sentry/react'
import { getAuthAdapter, getEmailInQueryParams, IAuthAdapter, logoutProps } from './providers'
import { getImage, profile } from '../Main/Services/User/api'
import { AccessControl } from '../refactory/modules/access-control/infrastructure/roles-permissions/access-control'
import { RolesPermissionsGlobalState } from '../refactory/modules/access-control/infrastructure/roles-permissions/roles-permissions-global-state'
import { useHookstate } from '@hookstate/core'
import Session from '../Main/Services/User/session'
import { KEY_LOCAL_STORAGE_USER_INFO } from './Auth.constants'
import { firebaseSignInWithCustomToken } from '../refactory/services/firebase/firebase'
import { ContainerSpin } from './Auth.styles'
import { getAuthProvider } from './providers'
import history from '../utils/history'
const ViewLogin = React.lazy(() => import('../refactory/modules/auth/infrastructure/login/Login'))

export interface AuthContextProps {
  loading: boolean
  loginError: boolean
  userInfo: UserInfo | null
  isAuthenticated: boolean
  logout: (props?: logoutProps) => Promise<void>
  updateUserInfo: (user: UserInfo | null) => void
  getUserProfile: () => Promise<void>
}
export const AuthContext = createContext<AuthContextProps | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [loginError, setLoginError] = useState(false)
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [shouldShowLogin, setShouldShowLogin] = useState(false)
  const { permissions } = useHookstate(RolesPermissionsGlobalState)
  const [emailDiscovery] = useState(() => getEmailInQueryParams())
  const [authProvider] = useState(() => getAuthProvider())
  const authAdapter: IAuthAdapter | undefined = getAuthAdapter(authProvider)

  useEffect(() => {
    if (shouldShowLogin) {
      history.push('/')
    }
  }, [shouldShowLogin])

  useEffect(() => {
    const initializeAuth = async () => {
      setLoginError(false)
      setLoading(true)
      setShouldShowLogin(false)
      try {
        if (!authProvider || !authAdapter) {
          setShouldShowLogin(true)
          return
        }

        await authAdapter.init()
        const authenticated = await authAdapter.isAuthenticated()
        setIsAuthenticated(authenticated)

        if (authenticated) {
          await authAdapter.generateToken()
          await getUserProfile()
        } else {
          await authAdapter.redirectLogin(emailDiscovery)
        }
      } catch (error) {
        console.error('Error during authentication initialization:', error)
        try {
          Sentry.captureException(error, {
            extra: {
              provider: authProvider,
              timestamp: new Date().toISOString(),
              userAgent: navigator.userAgent
            },
            tags: {
              context: 'auth_initialization'
            }
          })
        } catch (sentryError) {
          console.warn('Failed to send error to Sentry:', sentryError)
        }

        setLoginError(true)
        logout()
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const updateUserInfo = useCallback((userInfo: UserInfo | null) => {
    setUserInfo((_) => userInfo)
    Session.set(KEY_LOCAL_STORAGE_USER_INFO, userInfo)

    if (userInfo?.user) {
      Sentry.setUser({
        id: userInfo.user.email,
        username: `${userInfo.user.firstName} ${userInfo.user.lastName}`,
        email: userInfo.user.email
      })
      Sentry.setTag('avt_role_id', userInfo.user.avt_role_id)
      Sentry.setTag('avt_role_name', userInfo.user.avt_role?.name)
      Sentry.setTag('avt_role_super_admin', userInfo.user.avt_role?.super_admin)
      Sentry.setTag('client_number', userInfo.client?.clientNumber)
    }
  }, [])

  const getUserProfile = useCallback(async () => {
    try {
      const response: ProfileResponse = await profile()
      const { user, rules } = response

      const result = await AccessControl.defineAbilityForRole([], user)
      permissions.set(result.permissions)

      let clientLogoURL
      try {
        const { logoUrl } = user.client
        const clientLogoBlob = await getImage(logoUrl)
        clientLogoURL = URL.createObjectURL(clientLogoBlob)
      } catch (error) {
        console.error('Getting client logo blob', error)
      }

      await firebaseSignInWithCustomToken(user.firebaseToken, () => {})
      window.less.modifyVars(user.client.styles)
      updateUserInfo({ user, rules, client: user.client, clientLogoURL, isSSO: false })

      if (!user.is_user_in_new_provider || user.is_password_reset_required) {
        history.push('/change-password')
      }
    } catch (error) {
      console.error('Error getting user profile:', error)
      throw error
    }
  }, [updateUserInfo])

  const logout = useCallback(
    async (props?: logoutProps) => {
      updateUserInfo(null)
      setIsAuthenticated(false)
      Session.remove()
      if (authAdapter) {
        await authAdapter.logout(props)
      }
      setShouldShowLogin(true)
    },
    [authAdapter, updateUserInfo]
  )

  const contextValue: AuthContextProps = {
    isAuthenticated,
    userInfo,
    loading,
    loginError,
    logout,
    updateUserInfo,
    getUserProfile
  }

  return loading ? (
    <ContainerSpin>
      <Spin size='large' />
    </ContainerSpin>
  ) : isAuthenticated ? (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  ) : shouldShowLogin || !authProvider || !authAdapter ? (
    <React.Suspense
      fallback={
        <ContainerSpin>
          <Spin size='large' />
        </ContainerSpin>
      }
    >
      <ViewLogin />
    </React.Suspense>
  ) : (
    <AuthContext.Provider value={contextValue}>{authAdapter.getRenderLogin(emailDiscovery)}</AuthContext.Provider>
  )
}

export interface UserInfo extends ProfileResponse {
  client: Client
  clientLogoURL: string
  isSSO: boolean
}

export interface ProfileResponse {
  rules: Rule[]
  user: User
}

export interface Rule {
  action: string
  subject: string
}

export interface User {
  id: number
  firstName: string
  lastName: string
  email: string
  photoUrl: null
  isLive: boolean
  isOkInAuth0: boolean
  auth0Id: string
  workload: number
  lastAssignedFlag: null
  createdAt: Date
  updatedAt: Date
  avt_role_id: string
  deletedAt: null
  clientId: number
  roleId: number
  role: Role
  avt_role: AvtRole
  client: Client
  groups: any[]
  firebaseToken: string
  is_password_reset_required: boolean
  is_user_in_new_provider: boolean
  stytch_user_id: string | null
}

export interface AvtRole {
  name: string
  super_admin: boolean
  hierarchy: number
}

export interface Client {
  id: number
  name: string
  logoUrl: string
  clientNumber: string
  mainCity: string
  styles: string
  createdAt: Date
  updatedAt: Date
  cluster: string
  timeZone: string
}

export interface Role {
  name: string
}
